import {CommonModule, DatePipe} from '@angular/common';
import {HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi} from '@angular/common/http';
import {
  ApplicationConfig,
  computed,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  signal,
} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {DateAdapter, MAT_DATE_FORMATS, provideNativeDateAdapter} from '@angular/material/core';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from '@angular/material/form-field';
import {BrowserModule, provideClientHydration, withEventReplay} from '@angular/platform-browser';
import {
  InMemoryScrollingOptions,
  provideRouter,
  RouteReuseStrategy,
  TitleStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import {appInitializer} from '@app/app-initializer';
import {AuthInterceptor} from '@app/authentication/auth.interceptor';
import {NoReuseStrategy} from '@app/core/routing/no-reuse-strategy';
import {APP_ENVIRONMENT} from '@app/core/tokens/app-environment.token';
import {APP_SIGNALS, AppSignals} from '@app/core/tokens/app-signals.token';
import {UI_CONFIG} from '@app/core/tokens/ui-config.token';
import {environment} from '@environments/environment';
import {CategoriesSelectTreeComponent} from '@forms/categories-select-tree/categories-select-tree.component';
import {ChipListFormFieldComponent} from '@forms/chip-list-form-field/chip-list-form-field.component';
import {HipaaIdentifierChecklistComponent} from '@forms/hipaa-identifier-checklist/hipaa-identifier-checklist.component';
import {RepeatFormFieldComponent} from '@forms/repeat/repeat-form-field.component';
import {FormlyFieldTimepickerComponent} from '@forms/timepicker.component';
import {TinymceComponent} from '@forms/tinymce/tinymce.component';
import {CategoryTreeWrapper} from '@forms/wrappers/category-tree-wrapper';
import {CheckboxWrapper} from '@forms/wrappers/checkbox-wrapper';
import {GroupWrapperComponent} from '@forms/wrappers/group-wrapper';
import {CustomFieldTypes, FormlyFieldTypes} from '@models/form-field';
import {ConfigOption, FormlyFieldConfig, provideFormlyCore} from '@ngx-formly/core';
import {withFormlyMaterial} from '@ngx-formly/material';
import {withFormlyFieldDatepicker} from '@ngx-formly/material/datepicker';
import {DateFormatterService} from '@services/date/date-formatter.service';
import {provideBackendServices, provideMiddlewareServices} from '@services/provide-services';
import {
  ENVIRONMENT_PLACEHOLDER,
  HOSTNAME_PLACEHOLDER,
  INSTANCE_NAME_PLACEHOLDER,
  SEO_BOTS_META_PLACEHOLDER,
} from '@shared/constants/constants';
import {CustomTitleStrategy} from '@shared/title-strategy/custom-title-strategy';
import {
  OrganizationalEmailValidator,
  OrganizationalEmailValidatorMessage,
} from '@shared/validators/org-email.validator';
import {DayJSAdapter, MAT_DAYJS_DATE_FORMATS} from '@utilities/dayjs.adapter';
import {CategoriesSelectTreeRequiredValidator} from '@validators/categories-select-tree.validator';
import {DateRangeValidator, DateRangeValidatorMessage} from '@validators/date_range.validator';
import {DoiValidator, DoiValidatorMessage} from '@validators/doi.validator';
import {EmailValidator, EmailValidatorMessage} from '@validators/email.validator';
import {HttpsOnlyValidator, HttpsOnlyValidatorMessage} from '@validators/https-only.validator';
import {MulticheckboxRequiredValidator} from '@validators/multicheckbox.validator';
import {PhoneNumberValidator, PhoneNumberValidatorMessage} from '@validators/phone-number.validator';
import {ResourceUrlValidator, ResourceUrlValidatorMessage} from '@validators/resource-url.validator';
import {StateAbbreviationValidator, StateAbbreviationValidatorMessage} from '@validators/state-abbr.validator';
import {ZipCodeValidator, ZipCodeValidatorMessage} from '@validators/zip-code.validator';
import {Subscription} from 'rxjs';
import {routes} from './app.routes';

// Formly extension that clears a field's `touched` state whenever the user changes its value.
// This works in tandem with the global `showError` function (which only shows errors when a
// field is both invalid AND touched): once the user starts correcting an errored field, the
// error message disappears immediately and only reappears after the next blur event.
//
// Why `postPopulate`?
//   Formly calls `onPopulate` *before* merging the field's own config options, and
//   `postPopulate` *after*. Some fields (e.g. phone number fields) define their own
//   `hooks.onInit` in the form config (via `phoneNumberOnInit()`). If we used `onPopulate`,
//   `existingOnInit` would always be `undefined` for those fields and our wrapper would
//   silently overwrite their hook. Using `postPopulate` guarantees that `existingOnInit`
//   captures any hook already set by the form config, so both hooks run correctly.
const clearTouchedOnFocusExtension = {
  postPopulate(field: FormlyFieldConfig) {
    // this is actually used if your IDE says otherwise
    if (!field.formControl || field.fieldGroup) return;
    const existingOnInit = field.hooks?.onInit;
    const existingOnDestroy = field.hooks?.onDestroy;
    field.hooks = {
      ...field.hooks,
      onInit(f: FormlyFieldConfig) {
        if (existingOnInit) existingOnInit(f);

        // When the user starts editing a touched (errored) field, mark as untouched so the
        // error politely hides until the next blur event
        const sub: Subscription = f.formControl.valueChanges.subscribe(() => {
          if (f.formControl?.touched) {
            f.formControl.markAsUntouched({onlySelf: true});
          }
        });

        f.hooks = {
          ...f.hooks,
          onDestroy(fd: FormlyFieldConfig) {
            sub.unsubscribe();
            if (existingOnDestroy) existingOnDestroy(fd);
          },
        };
      },
    };
  },
};

export const customFormlyConfig: ConfigOption = {
  extras: {
    checkExpressionOn: 'changeDetectionCheck',
    showError: field => !!field.formControl?.invalid && !!field.formControl?.touched,
  },
  extensions: [{name: 'clear-touched-on-change', extension: clearTouchedOnFocusExtension}],
  types: [
    {
      name: FormlyFieldTypes.MULTICHECKBOX,
      defaultOptions: {
        validators: {
          required: MulticheckboxRequiredValidator,
        },
      },
    },
    {
      name: CustomFieldTypes.CHIP_LIST,
      component: ChipListFormFieldComponent,
    },
    {
      name: CustomFieldTypes.CATEGORY_TREE,
      component: CategoriesSelectTreeComponent,
      defaultOptions: {
        validators: {
          required: CategoriesSelectTreeRequiredValidator,
        },
      },
    },
    {name: CustomFieldTypes.RICH_TEXT, component: TinymceComponent},
    {name: CustomFieldTypes.TIMEPICKER, component: FormlyFieldTimepickerComponent, wrappers: ['form-field']},
    {name: CustomFieldTypes.REPEAT, component: RepeatFormFieldComponent},
    {name: CustomFieldTypes.HIPAA, component: HipaaIdentifierChecklistComponent, wrappers: ['form-field']},
  ],
  validators: [
    {name: 'email', validation: EmailValidator},
    {name: 'org-email', validation: OrganizationalEmailValidator},
    {name: 'dateRange', validation: DateRangeValidator},
    {name: 'https-only', validation: HttpsOnlyValidator},
    {name: 'state-abbr', validation: StateAbbreviationValidator},
    {name: 'zip-code', validation: ZipCodeValidator},
    {name: 'phone-number', validation: PhoneNumberValidator},
    {name: 'resource-url', validation: ResourceUrlValidator},
    {name: 'doi', validation: DoiValidator},
  ],
  validationMessages: [
    {name: 'required', message: 'This field is required'},
    {
      name: CustomFieldTypes.CATEGORY_TREE,
      message: 'At least one category must be selected.',
    },
    {name: 'email', message: EmailValidatorMessage},
    {name: 'org-email', message: OrganizationalEmailValidatorMessage},
    {name: 'dateRange', message: DateRangeValidatorMessage},
    {name: 'https-only', message: HttpsOnlyValidatorMessage},
    {name: 'state-abbr', message: StateAbbreviationValidatorMessage},
    {name: 'zip-code', message: ZipCodeValidatorMessage},
    {name: 'phone-number', message: PhoneNumberValidatorMessage},
    {name: 'resource-url', message: ResourceUrlValidatorMessage},
    {name: 'doi', message: DoiValidatorMessage},
  ],
  wrappers: [{name: 'group', component: GroupWrapperComponent}],
};

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled', // scrolls to anchor fragments if present
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({eventCoalescing: true}),
    provideRouter(routes, withComponentInputBinding(), withInMemoryScrolling(scrollConfig)),
    provideClientHydration(withEventReplay()),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    importProvidersFrom(BrowserModule, CommonModule, ReactiveFormsModule),
    provideAppInitializer(appInitializer),
    provideFormlyCore([
      ...withFormlyMaterial(),
      withFormlyFieldDatepicker(),
      customFormlyConfig,
      {
        wrappers: [
          {name: 'checkbox-wrapper', component: CheckboxWrapper},
          {name: 'category-tree-wrapper', component: CategoryTreeWrapper},
        ],
      },
    ]),
    ...provideNativeDateAdapter(),
    {provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: {appearance: 'outline'}},
    {provide: HTTP_INTERCEPTORS, multi: true, useClass: AuthInterceptor},
    {provide: LOCALE_ID, useValue: 'en-US'},
    {provide: DateAdapter, useClass: DayJSAdapter},
    {provide: MAT_DATE_FORMATS, useValue: MAT_DAYJS_DATE_FORMATS},
    {provide: RouteReuseStrategy, useClass: NoReuseStrategy},
    {provide: TitleStrategy, useClass: CustomTitleStrategy},
    {provide: APP_ENVIRONMENT, useValue: environment},
    {
      provide: UI_CONFIG,
      useFactory: () => signal<any>(null),
    },
    {
      provide: APP_SIGNALS,
      useFactory: (): AppSignals => {
        const appConfig = signal<any>(null);
        const seoBotsMeta = computed(() => appConfig()?.seo_bots_meta || SEO_BOTS_META_PLACEHOLDER);
        const hostname = computed(() => appConfig()?.hostname || HOSTNAME_PLACEHOLDER);
        const env = inject(APP_ENVIRONMENT);
        const envName = computed(() => {
          const fromApi = appConfig();
          const fromApiEnvName = fromApi?.envName;
          const fromEnv = env.envName;

          return fromApiEnvName && fromApiEnvName !== ENVIRONMENT_PLACEHOLDER
            ? fromApiEnvName
            : fromEnv || ENVIRONMENT_PLACEHOLDER;
        });

        const uiConfig = inject(UI_CONFIG);
        const instanceName = computed(() => {
          const config = uiConfig();
          return config?.config_data?.name || config?.name || INSTANCE_NAME_PLACEHOLDER;
        });

        return {
          appConfig,
          instanceName,
          seoBotsMeta,
          hostname,
          envName,
        };
      },
    },
    DatePipe,
    DateFormatterService,
    provideBackendServices(),
    provideMiddlewareServices(),
  ],
};
